import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ArrowUp, Loader, RotateCcw, ChevronDown, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { ScrollArea } from '../../components/ui/scroll-area';
import { cn } from '../../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from '../../components/ui/dropdown-menu';

import useLimits from '../plan/hooks/limits';
import { askMultiFileSummarizedQuestion } from '../../services/multiFileAskAIService';
import {
  getBubbleBaseStyles,
  getBubbleRoleStyles,
} from '../selectedVideo/chat/history/bubbleStyles';
import { ReferenceButton } from './ReferenceButton';
import { useUserTags } from '../../hooks/useTags';

interface EntityAskAIProps {
  entityName: string;
  entityType: 'person' | 'company';
  fileIds: string[];
  teamId: string;
  getSuggestions: (entityName: string) => string[];
}

interface SavedConversation {
  id: string;
  name: string;
  messages: Array<{ role: 'user' | 'assistant'; content: string }>;
  fileIds: string[];
  timestamp: number;
  entityKey?: string; // Added to track which entity this conversation belongs to
  entityName?: string;
  entityType?: string;
}

interface ConversationHistoryData {
  [entityKey: string]: SavedConversation[];
}

export const EntityAskAI: React.FC<EntityAskAIProps> = ({
  entityName,
  entityType,
  fileIds,
  teamId,
  getSuggestions,
}) => {
  const { t } = useTranslation();
  const guardLimits = useLimits();
  const { getTag, setTag } = useUserTags();

  const [question, setQuestion] = useState('');
  const [conversation, setConversation] = useState<
    Array<{ role: 'user' | 'assistant'; content: string }>
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [activeSource, setActiveSource] = useState<{
    fileId?: string;
    timestamp?: string;
  } | null>(null);
  const [transcriptView, setTranscriptView] = useState<{
    messageIndex: number;
    fileId: string;
    timestamp: string;
  } | null>(null);
  const [currentConversationId, setCurrentConversationId] = useState<string>(
    () => `conv-${Date.now()}`,
  );
  const [currentConversationEntityKey, setCurrentConversationEntityKey] =
    useState<string>(() => `${entityType}-${entityName}`);
  const [currentConversationFileIds, setCurrentConversationFileIds] =
    useState<string[]>(fileIds);
  const [savedConversations, setSavedConversations] = useState<
    SavedConversation[]
  >([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when conversation changes
  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollElement = scrollAreaRef.current.querySelector(
        '[data-radix-scroll-area-viewport]',
      );
      if (scrollElement) {
        scrollElement.scrollTop = scrollElement.scrollHeight;
      }
    }
  }, [conversation]);

  // Helper to get entity key for storage
  const getEntityKey = () => `${entityType}-${entityName}`;

  // Save conversation to history
  const saveCurrentConversation = useCallback(() => {
    if (conversation.length === 0) return;

    const conversationName = conversation[0]?.content || 'Untitled';
    const newSavedConversation: SavedConversation = {
      id: currentConversationId,
      name: conversationName,
      messages: conversation,
      fileIds: currentConversationFileIds,
      timestamp: Date.now(),
    };

    const historyString = getTag('entity-ask-ai-history');
    let allHistory: ConversationHistoryData = {};
    if (historyString) {
      try {
        allHistory = JSON.parse(historyString as string);
      } catch (error) {
        console.error('Failed to parse conversation history:', error);
      }
    }

    // Use the tracked entity key instead of the current entity
    const entityKey = currentConversationEntityKey;
    const entityConversations = allHistory[entityKey] || [];

    // Check if conversation already exists (by id) and update it, otherwise add new
    const existingIndex = entityConversations.findIndex(
      (conv) => conv.id === currentConversationId,
    );
    if (existingIndex >= 0) {
      entityConversations[existingIndex] = newSavedConversation;
    } else {
      entityConversations.push(newSavedConversation);
    }

    allHistory[entityKey] = entityConversations;
    setTag({
      key: 'entity-ask-ai-history',
      value: JSON.stringify(allHistory),
    });

    // Update savedConversations with ALL conversations globally
    const allConversations: SavedConversation[] = [];
    Object.entries(allHistory).forEach(([key, conversations]) => {
      const [entType, ...entNameParts] = key.split('-');
      const entName = entNameParts.join('-');

      conversations.forEach((conv) => {
        allConversations.push({
          ...conv,
          entityKey: key,
          entityName: entName,
          entityType: entType,
        });
      });
    });
    setSavedConversations(allConversations);
  }, [
    conversation,
    currentConversationId,
    currentConversationEntityKey,
    currentConversationFileIds,
  ]);

  // Load a conversation from history
  const loadConversation = (conversationId: string) => {
    const conversationToLoad = savedConversations.find(
      (conv) => conv.id === conversationId,
    );
    if (conversationToLoad) {
      setConversation(conversationToLoad.messages);
      setCurrentConversationId(conversationId);
      // Set the entity key to the original conversation's entity
      setCurrentConversationEntityKey(
        conversationToLoad.entityKey || getEntityKey(),
      );
      // Set the fileIds to the original conversation's files
      setCurrentConversationFileIds(conversationToLoad.fileIds || []);
      setQuestion('');
      setActiveSource(null);
      setTranscriptView(null);
    }
  };

  // Delete a conversation from history
  const deleteConversation = (conversationId: string, e?: React.MouseEvent) => {
    // Prevent the dropdown item click from firing
    e?.stopPropagation();

    const conversationToDelete = savedConversations.find(
      (conv) => conv.id === conversationId,
    );
    if (!conversationToDelete) return;

    const historyString = getTag('entity-ask-ai-history');
    let allHistory: ConversationHistoryData = {};
    if (historyString) {
      try {
        allHistory = JSON.parse(historyString as string);
      } catch (error) {
        console.error('Failed to parse conversation history:', error);
        return;
      }
    }

    const entityKey = conversationToDelete.entityKey || getEntityKey();
    const entityConversations = allHistory[entityKey] || [];

    // Remove the conversation
    const updatedEntityConversations = entityConversations.filter(
      (conv) => conv.id !== conversationId,
    );

    if (updatedEntityConversations.length === 0) {
      // If no conversations left for this entity, remove the entity key entirely
      delete allHistory[entityKey];
    } else {
      allHistory[entityKey] = updatedEntityConversations;
    }

    // Save updated history
    setTag({
      key: 'entity-ask-ai-history',
      value: JSON.stringify(allHistory),
    });

    // Update local state with all conversations globally
    const allConversations: SavedConversation[] = [];
    Object.entries(allHistory).forEach(([key, conversations]) => {
      const [entType, ...entNameParts] = key.split('-');
      const entName = entNameParts.join('-');

      conversations.forEach((conv) => {
        allConversations.push({
          ...conv,
          entityKey: key,
          entityName: entName,
          entityType: entType,
        });
      });
    });
    setSavedConversations(allConversations);

    // If we deleted the currently active conversation, start a new one
    if (conversationId === currentConversationId) {
      setConversation([]);
      setQuestion('');
      setCurrentConversationId(`conv-${Date.now()}`);
      setCurrentConversationEntityKey(getEntityKey());
      setCurrentConversationFileIds(fileIds);
      setActiveSource(null);
      setTranscriptView(null);
    }
  };

  // Load conversation history on mount - GLOBAL across all entities
  useEffect(() => {
    const historyString = getTag('entity-ask-ai-history');
    if (historyString) {
      try {
        const allHistory: ConversationHistoryData = JSON.parse(
          historyString as string,
        );

        // Flatten all conversations from all entities into a single array
        const allConversations: SavedConversation[] = [];
        Object.entries(allHistory).forEach(([entityKey, conversations]) => {
          // Add entity metadata to each conversation
          const [convEntityType, ...entityNameParts] = entityKey.split('-');
          const convEntityName = entityNameParts.join('-');

          conversations.forEach((conv) => {
            allConversations.push({
              ...conv,
              entityKey,
              entityName: convEntityName,
              entityType: convEntityType,
            });
          });
        });

        setSavedConversations(allConversations);
      } catch (error) {
        console.error('Failed to parse conversation history:', error);
      }
    }
  }, [getTag]);

  // Auto-save conversation whenever it changes
  useEffect(() => {
    if (conversation.length > 0) {
      saveCurrentConversation();
    }
  }, [
    conversation,
    currentConversationId,
    currentConversationEntityKey,
    currentConversationFileIds,
    saveCurrentConversation,
  ]);

  // When entity changes (navigating to different person/company), start fresh conversation
  useEffect(() => {
    const newEntityKey = getEntityKey();
    if (
      newEntityKey !== currentConversationEntityKey &&
      conversation.length > 0
    ) {
      // Save current conversation before switching
      saveCurrentConversation();
      // Start fresh for new entity
      setConversation([]);
      setQuestion('');
      setCurrentConversationId(`conv-${Date.now()}`);
      setCurrentConversationEntityKey(newEntityKey);
      setCurrentConversationFileIds(fileIds);
      setActiveSource(null);
      setTranscriptView(null);
    } else if (conversation.length === 0) {
      // If no conversation yet, just update the entity key and fileIds
      setCurrentConversationEntityKey(newEntityKey);
      setCurrentConversationFileIds(fileIds);
    }
  }, [entityName, entityType]);

  // When fileIds prop changes for current entity, update current conversation fileIds
  useEffect(() => {
    // Only update if we're on a new conversation (not loading an old one)
    const currentEntityKey = getEntityKey();
    if (
      currentConversationEntityKey === currentEntityKey &&
      conversation.length === 0
    ) {
      setCurrentConversationFileIds(fileIds);
    }
  }, [fileIds]);

  const handleAskQuestion = async (questionText?: string) => {
    const finalQuestion = questionText || question;

    if (!finalQuestion.trim()) {
      toast.error(
        t(`${entityType}.askAI.enterQuestion`, 'Please enter a question'),
      );
      return;
    }

    if (currentConversationFileIds.length === 0) {
      toast.error(
        t(
          `${entityType}.askAI.noRecordings`,
          `No recordings available for this ${entityType}`,
        ),
      );
      return;
    }

    try {
      guardLimits('multipleRecordingAskAI', async () => {
        // Add user message to conversation immediately
        const userMessage = { role: 'user' as const, content: finalQuestion };
        setConversation((prev) => [...prev, userMessage]);
        setQuestion(''); // Clear input immediately

        // Reset textarea height to single line
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto';
        }

        setIsLoading(true);

        try {
          const response = await askMultiFileSummarizedQuestion(teamId, {
            fileIds: currentConversationFileIds,
            question: finalQuestion,
          });

          if (response.success && response.data) {
            // Add assistant response to conversation
            const assistantMessage = {
              role: 'assistant' as const,
              content: response.data.answer.content,
            };
            setConversation((prev) => [...prev, assistantMessage]);
          } else {
            throw new Error('Invalid response from server');
          }
        } catch (error) {
          console.error('Failed to analyze recordings:', error);
          // Remove the user message on error
          setConversation((prev) => prev.slice(0, -1));
          setQuestion(finalQuestion); // Restore question on error
          toast.error(
            error instanceof Error
              ? error.message
              : t(
                  `${entityType}.askAI.error`,
                  'Failed to analyze recordings. Please try again.',
                ),
          );
        } finally {
          setIsLoading(false);
        }
      });
    } catch (error) {
      console.log('Guard limits reached:', error);
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !isLoading) {
      e.preventDefault();
      handleAskQuestion();
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    if (isLoading) return;
    setQuestion(suggestion);
    handleAskQuestion(suggestion);
  };

  const handleClearChat = () => {
    // Save current conversation before clearing
    if (conversation.length > 0) {
      saveCurrentConversation();
    }
    // Start a new conversation for the current entity
    setConversation([]);
    setQuestion('');
    setCurrentConversationId(`conv-${Date.now()}`);
    setCurrentConversationEntityKey(getEntityKey());
    setCurrentConversationFileIds(fileIds);
    setActiveSource(null);
    setTranscriptView(null);
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setQuestion(e.target.value);
    // Auto-resize textarea
    e.target.style.height = 'auto';
    const newHeight = Math.min(e.target.scrollHeight, 150);
    e.target.style.height = `${newHeight}px`;
  };

  const getPlaceholder = () => {
    if (currentConversationFileIds.length === 0) {
      return t(
        `${entityType}.askAI.placeholder.noFiles`,
        'No recordings available',
      );
    }
    const recordingText =
      currentConversationFileIds.length === 1 ? 'recording' : 'recordings';
    return t(
      `${entityType}.askAI.placeholder.withFiles`,
      `Ask anything (${currentConversationFileIds.length} ${recordingText})...`,
    );
  };

  const handleSourceClick = (fileId: string, timestamp: string) => {
    if (fileId && timestamp) {
      setActiveSource({ fileId, timestamp });
    } else {
      setActiveSource(null);
    }
  };

  // Convert plain text references [UUID]@timestamp to markdown links
  const convertReferencesToMarkdown = (text: string): string => {
    // Match pattern: [UUID]@timestamp where UUID is a standard UUID format
    const referenceRegex = /\[([a-f0-9-]{36})\]@(\d+)/g;
    return text.replace(referenceRegex, '[→]($1@$2)');
  };

  // Helper to categorize conversations by date
  const categorizeConversations = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const categorized = {
      today: [] as SavedConversation[],
      yesterday: [] as SavedConversation[],
      older: [] as SavedConversation[],
    };

    savedConversations
      .sort((a, b) => b.timestamp - a.timestamp)
      .forEach((conv) => {
        const convDate = new Date(conv.timestamp);
        const convDay = new Date(
          convDate.getFullYear(),
          convDate.getMonth(),
          convDate.getDate(),
        );

        if (convDay.getTime() === today.getTime()) {
          categorized.today.push(conv);
        } else if (convDay.getTime() === yesterday.getTime()) {
          categorized.yesterday.push(conv);
        } else {
          categorized.older.push(conv);
        }
      });

    return categorized;
  };

  // Custom markdown components to handle references
  const markdownComponents = (messageIndex: number) => ({
    a: ({ href, children }: { href?: string; children?: React.ReactNode }) => {
      // Handle UUID@timestamp references
      if (href?.includes('@')) {
        const [fileId, timestamp] = href.split('@');
        const isActive =
          activeSource?.fileId === fileId &&
          activeSource?.timestamp === timestamp;

        return (
          <ReferenceButton
            fileId={fileId}
            timestamp={timestamp}
            isActive={isActive}
            onActiveChange={(active) => {
              handleSourceClick(active ? fileId : '', active ? timestamp : '');
            }}
            onShowTranscript={() => {
              setTranscriptView({ messageIndex, fileId, timestamp });
            }}
          />
        );
      }

      // Regular external links
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline"
        >
          {children}
        </a>
      );
    },
    p: ({ children }: { children?: React.ReactNode }) => (
      <p className="mb-2 last:mb-0">{children}</p>
    ),
    ul: ({ children }: { children?: React.ReactNode }) => (
      <ul className="mb-2 last:mb-0 ml-4">{children}</ul>
    ),
    ol: ({ children }: { children?: React.ReactNode }) => (
      <ol className="mb-2 last:mb-0 ml-4">{children}</ol>
    ),
    li: ({ children }: { children?: React.ReactNode }) => (
      <li className="mb-1">{children}</li>
    ),
  });

  const categorized = categorizeConversations();

  return (
    <div className="flex flex-col h-full border-l border-gray-200 dark:border-gray-700">
      {/* Conversation Display - bottom-aligned */}
      <ScrollArea ref={scrollAreaRef} className="flex-1">
        <div className="min-h-full flex flex-col justify-end px-4 py-3">
          {/* Conversation History Dropdown - Scrollable with content */}
          <div className="pb-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="justify-between text-sm px-2 h-8"
                >
                  <span className="truncate">
                    {conversation.length > 0 && conversation[0]?.role === 'user'
                      ? conversation[0].content.slice(0, 30) +
                        (conversation[0].content.length > 30 ? '...' : '')
                      : 'New chat'}
                  </span>
                  <ChevronDown className="h-4 w-4 flex-shrink-0" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[300px]">
                {savedConversations.length > 0 ? (
                  <>
                    {categorized.today.length > 0 && (
                      <>
                        <DropdownMenuLabel className="text-xs text-muted-foreground">
                          Today
                        </DropdownMenuLabel>
                        {categorized.today.map((conv) => (
                          <DropdownMenuItem
                            key={conv.id}
                            onClick={() => loadConversation(conv.id)}
                            className={cn(
                              'group',
                              conv.id === currentConversationId && 'bg-accent',
                            )}
                          >
                            <div className="flex items-start gap-2 w-full">
                              <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                                <span className="text-sm truncate">
                                  {conv.name}
                                </span>
                                {conv.entityName && (
                                  <span className="text-xs text-muted-foreground truncate">
                                    {conv.entityName}
                                  </span>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={(e) => deleteConversation(conv.id, e)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded"
                              >
                                <X className="h-3 w-3 text-muted-foreground" />
                              </button>
                            </div>
                          </DropdownMenuItem>
                        ))}
                      </>
                    )}
                    {categorized.yesterday.length > 0 && (
                      <>
                        <DropdownMenuLabel className="text-xs text-muted-foreground">
                          Yesterday
                        </DropdownMenuLabel>
                        {categorized.yesterday.map((conv) => (
                          <DropdownMenuItem
                            key={conv.id}
                            onClick={() => loadConversation(conv.id)}
                            className={cn(
                              'group',
                              conv.id === currentConversationId && 'bg-accent',
                            )}
                          >
                            <div className="flex items-start gap-2 w-full">
                              <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                                <span className="text-sm truncate">
                                  {conv.name}
                                </span>
                                {conv.entityName && (
                                  <span className="text-xs text-muted-foreground truncate">
                                    {conv.entityName}
                                  </span>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={(e) => deleteConversation(conv.id, e)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded"
                              >
                                <X className="h-3 w-3 text-muted-foreground" />
                              </button>
                            </div>
                          </DropdownMenuItem>
                        ))}
                      </>
                    )}
                    {categorized.older.length > 0 && (
                      <>
                        <DropdownMenuLabel className="text-xs text-muted-foreground">
                          Older
                        </DropdownMenuLabel>
                        {categorized.older.map((conv) => (
                          <DropdownMenuItem
                            key={conv.id}
                            onClick={() => loadConversation(conv.id)}
                            className={cn(
                              'group',
                              conv.id === currentConversationId && 'bg-accent',
                            )}
                          >
                            <div className="flex items-start gap-2 w-full">
                              <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                                <span className="text-sm truncate">
                                  {conv.name}
                                </span>
                                {conv.entityName && (
                                  <span className="text-xs text-muted-foreground truncate">
                                    {conv.entityName}
                                  </span>
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={(e) => deleteConversation(conv.id, e)}
                                className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded"
                              >
                                <X className="h-3 w-3 text-muted-foreground" />
                              </button>
                            </div>
                          </DropdownMenuItem>
                        ))}
                      </>
                    )}
                  </>
                ) : (
                  <div className="px-2 py-4 text-sm text-muted-foreground text-center">
                    No saved conversations
                  </div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="flex flex-col gap-2">
            {conversation.map((message, index) => {
              // Check if this message is showing transcript view
              const isShowingTranscript =
                transcriptView?.messageIndex === index;

              if (isShowingTranscript && transcriptView) {
                return (
                  <ReferenceButton
                    key={`message-${index}`}
                    fileId={transcriptView.fileId}
                    timestamp={transcriptView.timestamp}
                    isActive={false}
                    onShowTranscript={() => {}}
                    inlineMode
                    onBack={() => setTranscriptView(null)}
                  />
                );
              }

              return (
                <div
                  key={`message-${index}`}
                  className={cn(
                    getBubbleBaseStyles(),
                    getBubbleRoleStyles(message.role),
                    'max-w-[85%]',
                  )}
                >
                  {message.role === 'assistant' ? (
                    <div className="prose prose-sm dark:prose-invert max-w-none">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={markdownComponents(index)}
                      >
                        {convertReferencesToMarkdown(message.content)}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <div className="whitespace-pre-wrap">{message.content}</div>
                  )}
                </div>
              );
            })}

            {/* Loading indicator */}
            {isLoading && (
              <div
                className={cn(
                  getBubbleBaseStyles(),
                  getBubbleRoleStyles('assistant'),
                  'max-w-[85%]',
                )}
              >
                <div className="flex items-center gap-1 min-h-[24px]">
                  <div className="w-2 h-2 bg-muted-foreground opacity-70 rounded-full animate-pulse [animation-delay:0s] [animation-duration:1.5s]" />
                  <div className="w-2 h-2 bg-muted-foreground opacity-70 rounded-full animate-pulse [animation-delay:0.2s] [animation-duration:1.5s]" />
                  <div className="w-2 h-2 bg-muted-foreground opacity-70 rounded-full animate-pulse [animation-delay:0.4s] [animation-duration:1.5s]" />
                </div>
              </div>
            )}
          </div>
        </div>
      </ScrollArea>

      {/* Suggestions - horizontally scrollable above grey section */}
      {conversation.length === 0 && currentConversationFileIds.length > 0 && (
        <div className="pb-2 flex items-end">
          <div
            className="flex gap-2 overflow-x-auto scrollbar-thin scrollbar-thumb-muted-foreground/20 scrollbar-track-transparent pb-2 px-4"
            style={{ scrollbarWidth: 'thin' }}
          >
            {getSuggestions(entityName).map((suggestion, index) => (
              <Button
                key={`suggestion-${index}`}
                variant="outline"
                size="sm"
                className={cn(
                  'flex-shrink-0 h-auto py-1 px-2 text-left whitespace-normal',
                  'border border-border/50 bg-background/50 hover:bg-accent/50',
                  'transition-all duration-200 ease-out',
                  'max-w-[200px] min-w-fit',
                )}
                onClick={() => handleSuggestionClick(suggestion)}
                disabled={isLoading}
              >
                <span className="text-sm font-medium line-clamp-1 truncate">
                  {suggestion}
                </span>
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Input Area - styled like chat Ask.tsx */}
      <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-background">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleAskQuestion();
          }}
        >
          <div
            className={cn(
              'bg-white dark:bg-background border shadow-sm',
              'transition-all duration-300 ease-in-out',
              'rounded-[20px]',
              isFocused
                ? 'border-primary shadow-md'
                : 'border-border shadow-sm',
              'px-1.5 py-1 flex items-center gap-1',
            )}
          >
            {/* Textarea */}
            <Textarea
              ref={textareaRef}
              className={cn(
                'flex-1 min-h-[24px] max-h-[150px] resize-none border-0 pl-1 pr-2 py-1',
                'text-sm leading-6 placeholder:text-muted-foreground',
                'focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent',
                'overflow-y-auto scrollbar-thin',
              )}
              placeholder={getPlaceholder()}
              value={question}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyPress}
              onFocus={(e) => {
                setIsFocused(true);
                e.target.style.height = 'auto';
                e.target.style.height = `${Math.min(e.target.scrollHeight, 150)}px`;
              }}
              onBlur={() => setIsFocused(false)}
              disabled={isLoading || currentConversationFileIds.length === 0}
              rows={1}
            />

            {/* Send/Reset Button */}
            <div className="flex-shrink-0">
              {(() => {
                const hasText = question.trim();
                const showReset =
                  !hasText && conversation.length > 0 && !isLoading;
                const isDisabled =
                  isLoading ||
                  (!hasText && !showReset) ||
                  currentConversationFileIds.length === 0;

                if (showReset) {
                  // Show reset button when no text and has conversation
                  return (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={handleClearChat}
                      className="h-7 w-7 rounded-full hover:bg-accent transition-all duration-200"
                    >
                      <RotateCcw className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  );
                }

                // Show send button
                const isInactive = !hasText && !isLoading;

                return (
                  <Button
                    type="submit"
                    size="icon"
                    disabled={isDisabled}
                    variant={isLoading || isInactive ? 'ghost' : 'default'}
                    className={cn(
                      'h-7 w-7 rounded-full transition-all duration-300',
                      (isLoading || isInactive) && 'bg-muted hover:bg-muted/80',
                      !isInactive &&
                        !isLoading &&
                        'bg-primary hover:bg-primary/90 hover:scale-105',
                    )}
                  >
                    {isLoading ? (
                      <Loader className="w-4 h-4 animate-spin text-muted-foreground" />
                    ) : (
                      <ArrowUp
                        className={cn(
                          'h-4 w-4 transition-all duration-300',
                          isInactive
                            ? 'text-muted-foreground'
                            : 'text-primary-foreground',
                          !isInactive && !isLoading && 'hover:-translate-y-0.5',
                        )}
                      />
                    )}
                  </Button>
                );
              })()}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EntityAskAI;
